import React, { useEffect, useRef, useState } from 'react'
import { BrowserMultiFormatReader } from '@zxing/browser'
import { CurrentSession } from '@/services/repository/session'
import { Services } from '@/services/services'
import { LoggedInPageLayout } from '../layout/LoggedInLayout'
import { isValidIMEI } from '@/common/validators'
import './scanner.css'
import { BackButton } from '../layout/NavBar'

interface QRScannerProps {
  services: Services
  onScan: (data: string) => void
}

export const QRScanner: React.FC<QRScannerProps> = ({ onScan }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [permissionDenied, setPermissionDenied] = useState(false)
  const [started, setStarted] = useState(true)
  const videoRef = useRef<HTMLVideoElement>(null)
  const controlsRef = useRef<{ stop: () => void } | null>(null)
  const permErrorMsg = 'Camera access denied. Please allow camera access in your browser settings.' 

  useEffect(() => {
    if (!started) return

    let cancelled = false
    const reader = new BrowserMultiFormatReader()

    const startScan = async () => {
      try {
        setIsLoading(true)
        setPermissionDenied(false)
        // **First: getUserMedia to ensure permission popup**
        await navigator.mediaDevices.getUserMedia({ video: true })

        // Now, list video devices
        const devices = await BrowserMultiFormatReader.listVideoInputDevices()

        // Try to select a back camera
        let selectedDevice = devices.find(
          (d) => d.label && d.label.toLowerCase().includes('back')
        )
        // Fallback: pick the second device (often back on iOS!)
        if (!selectedDevice && devices.length > 1) {
          selectedDevice = devices[1]
        }
        // Otherwise, use first device
        if (!selectedDevice && devices.length > 0) {
          selectedDevice = devices[0]
        }

        if (!selectedDevice || !videoRef.current) throw new Error('No camera device found')

        const controls = await reader.decodeFromVideoDevice(
          selectedDevice.deviceId,
          videoRef.current,
          (result: any, err: any, controls: any) => {
            if (cancelled) {
              controls.stop()
              return
            }
            setIsLoading(false)
            if (result) {
              onScan(result.getText())
              // controls.stop() // Uncomment for scan-once
            }
          }
        )
        controlsRef.current = controls
      } catch (error) {
        if (
          typeof error === 'object' &&
          error !== null &&
          'name' in error &&
          (
            (error as any).name === 'NotAllowedError' ||
            (error as any).name === 'PermissionDeniedError'
          )
        ) {
          setPermissionDenied(true)
        } else {
          console.error('Error initializing camera or scanning:', error)
        }
        setIsLoading(false)
      }
    }

    startScan()
    return () => {
      cancelled = true
      controlsRef.current?.stop()
      if (videoRef.current) {
        videoRef.current.srcObject = null
      }
    }
  }, [onScan, started])

  // UI: Only start scanner after click
  if (!started)
    return (
      <div className="qr-scanner">
        <button onClick={() => setStarted(true)}>Start QR Scanner</button>
        {permissionDenied && <div>{permErrorMsg}</div>}
      </div>
    )

  return (
    <div className='qr-scanner'>
      {isLoading ? <div className='spinner'></div> : null}
      {permissionDenied && <div className='camera-error'>{permErrorMsg}</div>}
      <video
        ref={videoRef}
        playsInline
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        muted
      />
    </div>
  )
}

export const QRScannerPage: React.FC<{ session: CurrentSession, services: Services }> = ({ services, session }) => {
  const [scannerData, setScannerData] = useState<string>()

  return (
    <LoggedInPageLayout pageName='qr-scanner' services={services} session={session}
      navbarLeft={<BackButton services={services} translates={session.translates} />}>
      <div>
        <h1>QR Code Scanner</h1>
        <QRScanner
          onScan={(data: string) => setScannerData(data)}
          services={services}
        />
        {scannerData
          ? isValidIMEI(scannerData)
            ? scannerData
            : `${scannerData} is not correct imei`
          : null}
      </div>
    </LoggedInPageLayout>
  )
}